import { Box } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  ProjectsWhereColumn,
  SqlOperator,
  useGetProjectCategoriesQuery,
} from "@src/__generated__/urql-graphql";
import { DataTable, Spinner } from "@src/components/ui-kit";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { can, withCan } from "@src/utils/components/permissions";
import { useStore, useUpdateURI } from "@src/utils/hooks";
import { getParamFromQuery } from "@src/utils/url";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { Fragment, type ReactElement, useEffect, useState } from "react";
import NavBar from "./components/navbar";
import FinishedSummary from "./FinishedSummary";
import FinishedTable from "./FinishedTable";
import { useFetchFilterOptions } from "./hooks";
import { Store, StoreContext } from "./Store";
import Summary from "./Summary";
import TableProjects from "./Table";

interface ListProps {
  brandId?: string;
  clientId?: string;
  hideFilters?: ProjectsWhereColumn.BrandId[];
  pageNavigation?: ReactElement;
}

const List = ({
  brandId,
  clientId,
  hideFilters,
  pageNavigation,
}: ListProps) => {
  const { query } = useRouter();
  const appStore = useStore();
  const [store] = useState(() => {
    const store = new Store(appStore);

    if (brandId) {
      store.where.implicitFilters.push({
        column: ProjectsWhereColumn.BrandId,
        value: brandId,
        operator: SqlOperator.Eq,
      });
    } else if (clientId) {
      store.where.implicitFilters.push({
        column: ProjectsWhereColumn.ClientId,
        value: clientId,
        operator: SqlOperator.Eq,
      });
    }

    if (hideFilters?.length) {
      hideFilters.forEach((i) => {
        store.where.filtersByColumn.get(i)?.hide();
      });
    }

    store.searchTerm = getParamFromQuery("search", query) ?? "";
    store.where.fromURLSearchParam(query);
    store.orderBy.fromURLSearchParam(query);
    store.pagination.fromURLSearchParam(query);

    return store;
  });

  const [{ data: categories }, fetchCategories] = useGetProjectCategoriesQuery({
    pause: true,
  });

  useEffect(() => {
    if (can("projectCategory_query")) {
      fetchCategories();
    }
  }, []);

  useEffect(() => {
    if (!categories?.projectCategories) return;
    store.where.filtersByColumn
      .get(ProjectsWhereColumn.CategoryId)
      ?.setOptions([]);

    categories.projectCategories.forEach(({ id, name }) => {
      store.where.filtersByColumn
        .get(ProjectsWhereColumn.CategoryId)
        ?.options.push({
          label: name,
          value: id,
        });
    });
  }, [categories]);

  useUpdateURI(store.searchParams, "id");

  useFetchFilterOptions(store);

  // useEffect(() => {
  //   store.fetchTableData();
  // }, [store.queryVariables]);

  return (
    <StoreContext.Provider value={store}>
      {store.isLoading && <Spinner absolute />}
      <DataTable>
        {pageNavigation ? (
          <>
            <Box mb="4" px="4" borderBottom="1px" borderBottomColor="gray.200">
              {pageNavigation}
            </Box>
            {store.isFinishedOrArchived ? (
              <FinishedSummary
                isLoaded={!store.isLoadingFinishedSummary}
                summary={store.finishedProjectsSummary}
                projectsCount={store.pagination.total ?? 0}
              />
            ) : (
              <Summary
                isLoaded={!store.isLoadingSummary}
                summary={store.projectsSummary}
                projectsCount={store.pagination.total ?? 0}
              />
            )}
            <DataTable.NavBar>
              <NavBar brandId={brandId} />
            </DataTable.NavBar>
            <DataTable.Filter>
              <FiltersPanel
                filters={store.where}
                searchPlaceholder={t`Search by brand, code, project`}
                searchDefaultValue={store.searchTerm}
                onSearch={(val) => {
                  store.searchTerm = val;
                  store.pagination.resetPage();
                }}
              />
            </DataTable.Filter>
            {store.isFinishedOrArchived ? (
              <FinishedTable fetching={store.isFetchingFinishedExtraData} />
            ) : (
              <TableProjects fetching={store.isFetchingExtraData} />
            )}
          </>
        ) : (
          <>
            <DataTable.NavBar>
              <NavBar brandId={brandId} />
            </DataTable.NavBar>
            <DataTable.Filter>
              <FiltersPanel
                filters={store.where}
                searchPlaceholder={t`Search by brand, code, project`}
                searchDefaultValue={store.searchTerm}
                onSearch={(val) => {
                  store.searchTerm = val;
                  store.pagination.resetPage();
                }}
              />
            </DataTable.Filter>
            {store.isFinishedOrArchived ? (
              <Fragment>
                <FinishedSummary
                  isLoaded={!store.isLoadingFinishedSummary}
                  summary={store.finishedProjectsSummary}
                  projectsCount={store.pagination.total ?? 0}
                />
                <FinishedTable fetching={store.isFetchingFinishedExtraData} />
              </Fragment>
            ) : (
              <Fragment>
                <Summary
                  isLoaded={!store.isLoadingSummary}
                  summary={store.projectsSummary}
                  projectsCount={store.pagination.total ?? 0}
                />
                <TableProjects fetching={store.isFetchingExtraData} />
              </Fragment>
            )}
          </>
        )}
      </DataTable>
    </StoreContext.Provider>
  );
};

export default withCan(
  {
    do: ["project_read_own", "project_read_all", "project_read_team"],
  },
  observer(List),
);
